import { Users, UsersRound, UserRound, Settings2, Tags, Briefcase } from "lucide-react";
import { PeopleSearch } from "./PeopleSearch";
import { useAudiences } from "@/lib/audiences";
import { V } from "@/lib/vocab";

const Opt = ({ id, active, onClick, icon: Icon, label, hint }) => (
  <button type="button" data-testid={`audience-${id}`} onClick={onClick}
    className={`w-full sm:w-auto sm:flex-1 sm:min-w-[140px] text-left rounded-2xl border px-3.5 py-3 transition-colors ${active ? "border-ink bg-sand-50" : "border-sand-200 bg-white hover:border-ink/40"}`}>
    <div className="flex items-center gap-2 font-heading font-bold text-sm text-ink"><Icon className="h-4 w-4 text-ink/70" strokeWidth={1.75} /> {label}</div>
    <p className="text-[11px] text-ink/55 mt-0.5 leading-snug">{hint}</p>
  </button>
);

// audience: { type: "all" | "audiences" | "group" | "people", keys: [chip keys], group_id: "", people: [{id, name, unit, ...}] }
export const AudiencePicker = ({ tenantId, audience, onChange, groups, onManageGroups }) => {
  const set = (patch) => onChange({ ...audience, ...patch });
  const group = groups.find((g) => g.id === audience.group_id);
  const aud = useAudiences(tenantId);
  const keys = audience.keys || [];
  const toggleKey = (k) => set({ keys: keys.includes(k) ? keys.filter((x) => x !== k) : [...keys, k] });
  const chipList = aud.list.filter((a) => a.key !== "staff" && (a.auto_source || "") !== "vendor_roster");
  return (
    <div data-testid="audience-picker">
      <label className="block text-xs font-bold text-ink/50 mb-2">To</label>
      <div className="flex flex-wrap gap-2">
        <Opt id="all" active={audience.type === "all"} onClick={() => set({ type: "all" })} icon={Users} label="Everyone" hint={`Every ${V.lc("person")} opted in on each channel.`} />
        <Opt id="audiences" active={audience.type === "audiences"} onClick={() => set({ type: "audiences" })} icon={Tags} label="Audiences" hint={`Owners, Renters, ${V.Grp()}, a custom chip — the same chips the knowledge base uses.`} />
        <Opt id="group" active={audience.type === "group"} onClick={() => set({ type: "group" })} icon={UsersRound} label="A saved group" hint="A street, a unit prefix, a hand-picked list…" />
        <Opt id="people" active={audience.type === "people"} onClick={() => set({ type: "people" })} icon={UserRound} label={`Specific ${V.lcs("person")}`} hint="One person or a handful — reads like a note from the office." />
      </div>

      {audience.type === "audiences" && (
        <div className="mt-3" data-testid="audience-chips">
          <div className="flex flex-wrap gap-1.5">
            {chipList.map((a) => {
              const on = keys.includes(a.key);
              return (
                <button key={a.key} type="button" data-testid={`audience-chip-${a.key}`} onClick={() => toggleKey(a.key)} aria-pressed={on} title={a.description || a.label}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-sand-200 hover:border-ink/40"}`}>
                  {a.group === "team" ? <Briefcase className="h-3 w-3" /> : <Users className="h-3 w-3" />} {a.label}
                  <span className={`tabular-nums ${on ? "text-white/60" : "text-ink/40"}`}>{a.members}</span>
                </button>
              );
            })}
            {aud.loading && <span className="text-xs text-ink/50">Loading audiences…</span>}
          </div>
          <p data-testid="audience-chips-summary" className="mt-1.5 text-[11px] text-ink/50">
            {keys.length ? `Goes to anyone carrying ${keys.map((k) => aud.labels[k] || k).join(" or ")}. ${V.Grp()} and Managers reach team members who are also on the roster.` : "Pick one or more chips."}
          </p>
        </div>
      )}

      {audience.type === "group" && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <select data-testid="audience-group-select" value={audience.group_id || ""} onChange={(e) => set({ group_id: e.target.value })}
            className="flex-1 rounded-xl border border-sand-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30">
            <option value="">{groups.length ? "Pick a group…" : "No groups yet — create one"}</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.member_count} {g.member_count === 1 ? "person" : "people"}</option>)}
          </select>
          <button type="button" data-testid="audience-manage-groups" onClick={onManageGroups}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 py-2 text-xs font-semibold text-ink/70 hover:border-ink hover:text-ink whitespace-nowrap">
            <Settings2 className="h-3.5 w-3.5" /> Manage groups
          </button>
        </div>
      )}
      {audience.type === "group" && group && (
        <p data-testid="audience-group-summary" className="mt-1.5 text-[11px] text-ink/50">
          {group.kind === "smart" ? "Smart group — membership follows the roster automatically." : "Hand-picked list."} {group.member_count} on it right now.
        </p>
      )}

      {audience.type === "people" && (
        <div className="mt-3">
          <PeopleSearch tenantId={tenantId} selected={audience.people || []} onChange={(people) => set({ people })} testId="audience-people" />
          <p className="mt-1.5 text-[11px] text-ink/50">Texts to specific {V.lcs("person")} skip the STOP footer and land in their existing conversation thread — replies show up in the Inbox.</p>
        </div>
      )}
    </div>
  );
};
